const { POLICY_APPLICATIONS, FLOW_POLICY_APPLICATIONS } = require('@kentik/ui-shared/alerting/constants');

/**
 * Canonical Application Format
 *
 * Throughout alerting, application is used to identify and segment policies, rules, alerts, and notifications.
 * For a while these were just strings: "core", "ddos", or "nms".
 *
 * In order to get more granular segments, we introduced a dot notation namespace, like an internet domain,
 * where the namespace goes from most to least specific, right to left.
 *
 * Subpolicies use a format like <tenantID>.mkp.{ddos|core}.
 * Ex: "1234.mkp.ddos" returns all ddos results for tenant 1234.
 * Ex: "*.mkp.ddos" returns all ddos results for all tenants.
 * Ex: "*.ddos" returns all results, tenant-agnostic.
 * Ex: "ddos" returns only results with an application exactly matching "ddos".
 *
 */

const TENANT_NAMESPACE = 'mkp';

function getTopLevelApplication(app) {
  return app?.split('.').pop() || POLICY_APPLICATIONS.CORE;
}

function extractTenantId(app) {
  const parts = app?.split('.') || [];
  return parts[1] === TENANT_NAMESPACE ? parts[0] : null;
}

// For strings
const isApplication = {
  isCore: (app) => app?.endsWith(POLICY_APPLICATIONS.CORE) || app === '',
  isCloud: (app) => app?.endsWith(POLICY_APPLICATIONS.CLOUD),
  isDdos: (app) => app?.endsWith(POLICY_APPLICATIONS.DDOS),
  isNms: (app) => app?.endsWith(POLICY_APPLICATIONS.NMS),
  isEvent: (app) => app?.endsWith(POLICY_APPLICATIONS.KEVENT),
  isMetric: (app) => app?.endsWith(POLICY_APPLICATIONS.METRIC),
  isSynth: (app) => app?.endsWith(POLICY_APPLICATIONS.SYNTHETICS)
};

// For arrays
const checkApplication = Object.assign({}, isApplication, {
  hasCore: (apps = []) => apps?.some(isApplication.isCore),
  hasCloud: (apps = []) => apps?.some(isApplication.isCloud),
  hasDdos: (apps = []) => apps?.some(isApplication.isDdos),
  hasNms: (apps = []) => apps?.some(isApplication.isNms),
  hasEvent: (apps = []) => apps?.some(isApplication.isEvent),
  hasMetric: (apps = []) => apps?.some(isApplication.isMetric),
  hasSynth: (apps = []) => apps?.some(isApplication.isSynth)
});

function formatSubpolicyApplication(applicationString = '', tenantId) {
  if (!tenantId || tenantId === '0') {
    throw new Error('Cannot process subpolicy without a valid tenant ID');
  }

  const topLevelApp = getTopLevelApplication(applicationString);

  return `${tenantId}.${TENANT_NAMESPACE}.${topLevelApp}`;
}

function scopeApplicationsToTenant(applications, tenantId) {
  return applications?.length
    ? applications.map((app) => formatSubpolicyApplication(app, tenantId))
    : [formatSubpolicyApplication('', tenantId)];
}

function buildApplicationFilter(applications = [], options) {
  const { userGroup, includeSubpolicies, tenants = [] } = options || {};

  // CRITICAL: Prefix applications with tenant namespace to prevent cross-tenant data leakage
  if (userGroup) {
    return scopeApplicationsToTenant(applications, userGroup.id);
  }

  // Filtering by specific tenants (excludes non-tenant results)
  if (tenants?.length > 0) {
    return tenants.flatMap((tenantId) => applications.map((app) => formatSubpolicyApplication(app, tenantId)));
  }

  // Include any tenant results (*) in addition to non-tenant results
  if (includeSubpolicies) {
    return applications.concat(applications.map((app) => formatSubpolicyApplication(app, '*')));
  }

  return applications;
}

// This function takes in requested applications, and expands the list based on what's supported.
// For example, a request by the user for 'NMS' alerts implies `kevent`, `nms` and `kmetrics` applications.

function getExpandedApplications(requestedApplications = [], supportedApplications = []) {
  const base = requestedApplications?.length ? requestedApplications : FLOW_POLICY_APPLICATIONS;
  const appSet = new Set(base);
  const hasNmsOrMetric = checkApplication.hasNms(base) || checkApplication.hasMetric(base);

  if (hasNmsOrMetric) {
    [POLICY_APPLICATIONS.NMS, POLICY_APPLICATIONS.METRIC, POLICY_APPLICATIONS.KEVENT].forEach((app) => {
      if (supportedApplications.includes(app)) {
        appSet.add(app);
      }
    });
  }

  return Array.from(appSet);
}

module.exports = {
  extractTenantId,
  formatSubpolicyApplication,
  scopeApplicationsToTenant,
  buildApplicationFilter,
  getTopLevelApplication,
  checkApplication,
  getExpandedApplications
};
